import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Search, Plus, CheckCircle, Clock } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { releveService } from '../../services/api.service';
import { ReleveNotes } from '../../types';
import ScrollToTop from '../../components/ui/ScrollToTop';

interface ReleveWithDetails {
  id: number;
  etudiant: {
    matricule: string;
    nom: string;
    prenom: string;
    niveau: string;
  };
  type_releve: 'semestre' | 'annuel' | 'partiel';
  semestre?: number;
  annee_academique: string;
  date_generation: string;
  code_verification: string;
  fichier_pdf?: string;
}

export const RelevesList: React.FC = () => {
  const navigate = useNavigate();
  const [releves, setReleves] = useState<ReleveWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    niveau: '',
    type_releve: '',
    semestre: '',
  });

  useEffect(() => {
    fetchReleves();
  }, [filters]);

  const fetchReleves = async () => {
    try {
      setLoading(true);
      const response = await releveService.getAll({
        search: searchQuery,
        ...filters,
      });
      setReleves(response.results as any || []);
    } catch (error) {
      console.error('Erreur lors du chargement des relevés:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchReleves();
  };

  const handleGenererReleve = () => {
    navigate('/releves/notes');
  };

  const handleDownload = async (releveId: number) => {
    try {
      const blob = await releveService.telecharger(releveId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `releve_${releveId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du relevé');
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'semestriel':
        return 'info';
      case 'annuel':
        return 'success';
      case 'definitif':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading && releves.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Relevés de Notes PDF</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Génération et consultation des relevés officiels</p>
        </div>
        <Button variant="primary" icon={<Plus size={20} />} onClick={handleGenererReleve}>
          Générer des Relevés
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Relevés</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{releves.length}</h3>
            </div>
            <FileText className="text-blue-600" size={32} />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ce Mois</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">156</h3>
            </div>
            <Clock className="text-green-600" size={32} />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Vérifiés</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">1,234</h3>
            </div>
            <CheckCircle className="text-purple-600" size={32} />
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Rechercher par matricule, nom ou code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <select
              value={filters.niveau}
              onChange={(e) => setFilters({ ...filters, niveau: e.target.value })}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les niveaux</option>
              <option value="L1">L1</option>
              <option value="L2">L2</option>
              <option value="L3">L3</option>
              <option value="M1">M1</option>
              <option value="M2">M2</option>
            </select>

            <select
              value={filters.type_releve}
              onChange={(e) => setFilters({ ...filters, type_releve: e.target.value })}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les types</option>
              <option value="semestriel">Semestriel</option>
              <option value="annuel">Annuel</option>
              <option value="definitif">Définitif</option>
            </select>
          </div>

          <Button type="submit" variant="primary">
            <Search size={16} className="mr-2" />
            Rechercher
          </Button>
        </form>
      </Card>

      {/* Releves Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Étudiant
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Semestre
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Année
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Date Génération
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Code Vérification
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {releves.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Aucun relevé trouvé
                  </td>
                </tr>
              ) : (
                releves.map((releve) => (
                  <tr key={releve.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {releve.etudiant.nom} {releve.etudiant.prenom}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{releve.etudiant.matricule}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <Badge variant={getTypeColor(releve.type_releve)}>
                        {releve.type_releve}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">
                      {releve.semestre ? `S${releve.semestre}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">
                      {releve.annee_academique}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                      {new Date(releve.date_generation).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                        {releve.code_verification.substring(0, 12)}...
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Download size={16} />}
                        onClick={() => handleDownload(releve.id)}
                      >
                        Télécharger
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-lg transition cursor-pointer">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <FileText className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Génération par Lot</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Générer plusieurs relevés à la fois</p>
            </div>
          </div>
        </Card>

        <Card className="hover:shadow-lg transition cursor-pointer">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-600 dark:text-green-400" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Vérifier un Relevé</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Vérifier l'authenticité avec le code</p>
            </div>
          </div>
        </Card>

        <Card className="hover:shadow-lg transition cursor-pointer">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
              <Download className="text-purple-600 dark:text-purple-400" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Export en Masse</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Télécharger tous les relevés</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};
